import React, { useState } from 'react';
import { 
  viewDocument, 
  hasGoogleDriveFile, 
  isLegacyFile, 
  getDocumentStatus, 
  formatFileSize, 
  formatUploadDate, 
  getGoogleDrivePath 
} from '../../../../utils/googleDriveHelpers';
import styles from './DocumentGallery.module.css';

/**
 * DocumentGallery - Phase 3D
 * 
 * Enhanced document viewer with:
 * - Document grid layout
 * - Status indicators
 * - Google Drive integration
 * - File metadata display
 * - Loading and error states
 */
const DocumentGallery = ({ 
  application, 
  onViewDocument,
  showMetadata = true,
  compact = false
}) => {
  const [loadingFiles, setLoadingFiles] = useState(new Set());
  const [errorMessages, setErrorMessages] = useState({});

  // Handle document viewing
  const handleViewDocument = async (applicationId, documentType, fileIndex, fileName) => {
    const fileKey = `${documentType}-${fileIndex}`;
    
    setLoadingFiles(prev => new Set([...prev, fileKey]));
    setErrorMessages(prev => ({ ...prev, [fileKey]: null }));
    
    // Optional callback
    onViewDocument?.(applicationId, documentType, fileIndex, fileName);

    try {
      await viewDocument(
        applicationId, 
        documentType, 
        fileIndex, 
        fileName,
        (errorMsg) => {
          setErrorMessages(prev => ({ ...prev, [fileKey]: errorMsg }));
        }
      );
    } catch (error) {
      setErrorMessages(prev => ({ ...prev, [fileKey]: error.message }));
    } finally {
      setLoadingFiles(prev => {
        const newSet = new Set([...prev]);
        newSet.delete(fileKey);
        return newSet;
      });
    }
  };

  // Get file icon based on type
  const getFileIcon = (mimeType) => {
    if (!mimeType) return '📄';
    if (mimeType.includes('pdf')) return '📕';
    if (mimeType.includes('image')) return '🖼️';
    if (mimeType.includes('word') || mimeType.includes('document')) return '📘';
    if (mimeType.includes('excel') || mimeType.includes('spreadsheet')) return '📗';
    return '📄';
  };

  // Format document title
  const formatDocTitle = (requirementId) => {
    return requirementId
      .replace(/_/g, ' ')
      .replace(/([A-Z])/g, ' $1')
      .replace(/^./, str => str.toUpperCase())
      .trim();
  };

  if (!application?.documents || application.documents.length === 0) {
    return (
      <div className={`${styles.documentGallery} ${compact ? styles.compact : ''}`}>
        <div className={styles.emptyState}>
          <span className={styles.emptyIcon}>📎</span>
          <p>No documents uploaded</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`${styles.documentGallery} ${compact ? styles.compact : ''}`}>
      <div className={styles.documentsGrid}>
        {application.documents.map((document, docIndex) => {
          const statusInfo = getDocumentStatus(document);
          
          return (
            <div key={docIndex} className={styles.documentCard}>
              <div className={styles.cardHeader}>
                <h4>{formatDocTitle(document.requirementId)}</h4>
                <span className={`${styles.statusBadge} ${styles[statusInfo.className] || ''}`}>
                  {statusInfo.icon} {statusInfo.label}
                </span>
              </div>
              
              {document.files && document.files.length > 0 ? (
                <div className={styles.filesList}>
                  {document.files.map((file, fileIndex) => {
                    const fileKey = `${document.requirementId}-${fileIndex}`;
                    const isLoading = loadingFiles.has(fileKey);
                    const errorMessage = errorMessages[fileKey];
                    const canView = hasGoogleDriveFile(file);
                    const isLegacy = isLegacyFile(file);
                    
                    return (
                      <div key={fileIndex} className={styles.fileItem}>
                        <button
                          className={`${styles.fileButton} ${
                            canView ? styles.viewable : 
                            isLegacy ? styles.legacy : styles.unavailable
                          }`}
                          onClick={() => handleViewDocument(
                            application._id, 
                            document.requirementId, 
                            fileIndex, 
                            file.originalName
                          )}
                          disabled={isLoading || !canView}
                          title={
                            canView ? `View ${file.originalName}` :
                            isLegacy ? 'Legacy file - may not be available' :
                            'File not available'
                          }
                        >
                          {isLoading ? (
                            <span className={styles.loadingText}>
                              <span className={styles.fileSpinner}></span>
                              Loading...
                            </span>
                          ) : (
                            <>
                              <span className={styles.fileIcon}>
                                {getFileIcon(file.mimeType)}
                              </span>
                              <span className={styles.fileName}>
                                {file.originalName}
                              </span>
                              <div className={styles.fileIndicators}>
                                {canView && (
                                  <span className={styles.driveIcon} title="Stored in Google Drive">☁️</span>
                                )}
                                {isLegacy && (
                                  <span className={styles.legacyIcon} title="Legacy storage">🗂️</span>
                                )}
                              </div>
                            </>
                          )}
                        </button>
                        
                        {errorMessage && (
                          <div className={styles.errorMessage}>
                            ⚠️ {errorMessage}
                          </div>
                        )}
                        
                        {showMetadata && !compact && (
                          <div className={styles.fileMetadata}>
                            <span className={styles.metaItem}>
                              📦 {formatFileSize(file.size)}
                            </span>
                            <span className={styles.metaItem}>
                              📁 {file.mimeType?.split('/')[1]?.toUpperCase() || 'FILE'}
                            </span>
                            <span className={styles.metaItem}>
                              📅 {formatUploadDate(file.uploadDate)}
                            </span>
                            {file.driveFullPath && (
                              <span className={styles.metaItem} title={file.driveFullPath}>
                                📍 {getGoogleDrivePath(file)}
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className={styles.noFiles}>
                  <span>No files uploaded for this document</span>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default DocumentGallery;
